import {
  FilterOperations,
  IAdvancedFilterGroup,
  IFilter,
  IFilterGroup
} from '../../settings-new/models/general/advanced-filter-group.interface';
import {SharedFilterState} from './shared-filter.reducer';
import {getDeepCopyOfObject} from '../../../shared/shared-utils/object/object.utils';

export function addFilterOrGroupToGroup(filterGroup: IFilterGroup, newItem: IFilter | IFilterGroup, predecessorId: number): IFilterGroup {
  if (filterGroup.id === predecessorId) {
    filterGroup.filtersAndGroups.push(newItem);
    return filterGroup;
  }
  filterGroup.filtersAndGroups = filterGroup.filtersAndGroups.map((item) => {
    if ('filtersAndGroups' in item) {
      return addFilterOrGroupToGroup(item, newItem, predecessorId);
    }
    return item;
  });
  return filterGroup;
}

export function findAndReplaceFilterWithinAdvancedFilterGroup(
  advancedFilterGroup: IAdvancedFilterGroup,
  newFilter: IFilter
): IAdvancedFilterGroup {
  const replacementResult = replaceFilterInFilterGroup(advancedFilterGroup.filterGroup, newFilter);
  if (!replacementResult.replaced) {
    return advancedFilterGroup;
  }
  return {
    ...advancedFilterGroup,
    filterGroup: replacementResult.filterGroup,
  };
}

function replaceFilterInFilterGroup(
  filterGroup: IFilterGroup,
  newFilter: IFilter,
): { replaced: boolean; filterGroup: IFilterGroup } {
  let replaced = false;
  const filtersAndGroups = filterGroup.filtersAndGroups.map((item: IFilter | IFilterGroup) => {
    if ('filtersAndGroups' in item) {
      const nestedResult = replaceFilterInFilterGroup(item, newFilter);
      if (nestedResult.replaced) {
        replaced = true;
        return nestedResult.filterGroup;
      }
      return item;
    }
    if (item.id === newFilter.id) {
      replaced = true;
      return newFilter;
    }
    return item;
  });

  if (!replaced) {
    return {replaced: false, filterGroup};
  }

  return {
    replaced: true,
    filterGroup: {
      ...filterGroup,
      filtersAndGroups,
    },
  };
}

export function getCopyOfAdvancedFilterGroup(advancedFilterGroup: IAdvancedFilterGroup, saved: boolean, date: Date): IAdvancedFilterGroup {
  const selectedAdvancedFilterGroup = getDeepCopyOfObject(advancedFilterGroup);
  selectedAdvancedFilterGroup.saved = saved;
  selectedAdvancedFilterGroup.lastModified = date;
  return selectedAdvancedFilterGroup;
}

export function onCreateAdvancedFilterGroup(advancedFilterGroups: IAdvancedFilterGroup[], newAdvancedFilterGroup: IAdvancedFilterGroup): IAdvancedFilterGroup[] {
  const copyOfAdvancedFilterGroups = getDeepCopyOfObject(advancedFilterGroups);
  copyOfAdvancedFilterGroups.unshift(newAdvancedFilterGroup);
  return copyOfAdvancedFilterGroups;
}

export function onGenerateCopyOfAdvancedFilterGroup(
  advancedFilterGroup: IAdvancedFilterGroup,
  advancedFilterIDCounter: number
): IAdvancedFilterGroup {
  const copy = getDeepCopyOfObject(advancedFilterGroup);
  return {
    ...copy,
    id: advancedFilterIDCounter + 1,
    name: `Copy of ${advancedFilterGroup.name}`,
    saved: false,
    lastModified: new Date()
  } as IAdvancedFilterGroup;
}

export function onGenerateNewAdvancedFilterGroup(advancedFilterGroupLength: number, advancedFilterIDCounter: number, grid: string): IAdvancedFilterGroup {
  const advancedFilterGroupId = advancedFilterIDCounter + 1;
  const containerId = advancedFilterIDCounter + 2;
  const firstGroupId = advancedFilterIDCounter + 3;
  const firstFilterId = advancedFilterIDCounter + 4;
  return {
    filterGroup: {
      id: containerId,
      operator: 'AND',
      filtersAndGroups: [{
        id: firstGroupId,
        operator: 'AND',
        filtersAndGroups: [{id: firstFilterId} as IFilter],
      }],
    },
    grid,
    id: advancedFilterGroupId,
    isApplied: false,
    name: `${advancedFilterGroupLength + 1} New Advanced Filter Group`,
    saved: false,
    lastModified: new Date(),
  } as IAdvancedFilterGroup;
}

// ====================================================================================
// Action Helpers
// ====================================================================================
export function onActionAddFilterAndGroupToSelectedAdvancedFilterGroup(state: SharedFilterState, predecessorID: number, shouldAddFilter: boolean): SharedFilterState {
  const selectedAdvancedFilterGroup = getCopyOfAdvancedFilterGroup(
    state.selectedAdvancedFilterGroup,
    false,
    new Date(new Date().setMonth(new Date().getMonth() + 1))
  );
  const containerId = state.advancedFilterIDCounter + 1;
  const firstGroupId = state.advancedFilterIDCounter + 2;
  const firstFilterId = state.advancedFilterIDCounter + 3;
  const firstGroup: IFilterGroup = {
    id: firstGroupId,
    operator: 'AND',
    filtersAndGroups: shouldAddFilter ? [{id: firstFilterId} as IFilter] : [],
  };
  selectedAdvancedFilterGroup.filterGroup = {
    id: containerId,
    operator: 'AND',
    filtersAndGroups: [firstGroup],
  };
  const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
  const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === selectedAdvancedFilterGroup.id);
  advancedFilterGroups[index] = selectedAdvancedFilterGroup;
  return {
    ...state,
    advancedFilterGroups,
    advancedFilterIDCounter: shouldAddFilter ? state.advancedFilterIDCounter + 3 : state.advancedFilterIDCounter + 2,
    selectedAdvancedFilterGroup
  }
}

export function onActionAddFilterToSelectedAdvancedFilterGroup(state: SharedFilterState, predecessorID: number): SharedFilterState {
  const selectedAdvancedFilterGroup = getCopyOfAdvancedFilterGroup(
    state.selectedAdvancedFilterGroup,
    false,
    new Date(new Date().setMonth(new Date().getMonth() + 1))
  );
  selectedAdvancedFilterGroup.filterGroup = addFilterOrGroupToGroup(
    selectedAdvancedFilterGroup.filterGroup,
    {id: state.advancedFilterIDCounter + 1} as IFilter,
    predecessorID,
  );
  const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
  const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === selectedAdvancedFilterGroup.id);
  advancedFilterGroups[index] = selectedAdvancedFilterGroup;
  return {
    ...state,
    advancedFilterGroups,
    advancedFilterIDCounter: state.advancedFilterIDCounter + 1,
    selectedAdvancedFilterGroup
  }
}

export function onActionAddGroupToSelectedAdvancedFilterGroup(state: SharedFilterState, predecessorID: number): SharedFilterState {
  const selectedAdvancedFilterGroup = getCopyOfAdvancedFilterGroup(
    state.selectedAdvancedFilterGroup,
    false,
    new Date(new Date().setMonth(new Date().getMonth() + 1))
  );
  const newGroupId = state.advancedFilterIDCounter + 1;
  const newFilterId = state.advancedFilterIDCounter + 2;
  const newSubgroup: IFilterGroup = {
    id: newGroupId,
    operator: 'AND',
    filtersAndGroups: [{id: newFilterId} as IFilter],
  };
  if (predecessorID === selectedAdvancedFilterGroup.filterGroup.id) {
    selectedAdvancedFilterGroup.filterGroup.filtersAndGroups = [
      ...selectedAdvancedFilterGroup.filterGroup.filtersAndGroups,
      newSubgroup,
    ];
  } else {
    selectedAdvancedFilterGroup.filterGroup = addFilterOrGroupToGroup(
      selectedAdvancedFilterGroup.filterGroup,
      newSubgroup,
      predecessorID,
    );
  }
  const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
  const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === selectedAdvancedFilterGroup.id);
  advancedFilterGroups[index] = selectedAdvancedFilterGroup;
  return {
    ...state,
    advancedFilterGroups,
    advancedFilterIDCounter: state.advancedFilterIDCounter + 2,
    selectedAdvancedFilterGroup,
  };
}


export function deleteFilterWithinAdvancedFilterGroup(
  advancedFilterGroup: IAdvancedFilterGroup,
  filterId: number
): IAdvancedFilterGroup {
  const {filterGroup} = advancedFilterGroup;
  filterGroup.filtersAndGroups = filterGroup.filtersAndGroups.filter((item: IFilterGroup | IFilter): boolean => {
    if ('id' in item && item.id === filterId) {
      return false;
    }
    if ('filtersAndGroups' in item) {
      const filteredGroup = deleteFilterWithinAdvancedFilterGroup({
        filterGroup: item,
        grid: advancedFilterGroup.grid,
        id: advancedFilterGroup.id,
        name: advancedFilterGroup.name,
        saved: advancedFilterGroup.saved
      }, filterId);
      const index = filterGroup.filtersAndGroups.indexOf(item);
      filterGroup.filtersAndGroups[index] = filteredGroup.filterGroup;
    }
    return true;
  });

  return advancedFilterGroup;
}

export function deleteFilterOrGroupWithinAdvancedFilterGroup(
  advancedFilterGroup: IAdvancedFilterGroup,
  filterId: number
): IAdvancedFilterGroup {
  const {filterGroup} = advancedFilterGroup;
  filterGroup.filtersAndGroups = filterGroup.filtersAndGroups.filter((item: IFilterGroup | IFilter): boolean => {
    if ('id' in item && item.id === filterId) {
      return false;
    }
    if ('filtersAndGroups' in item) {
      const filteredGroup = deleteFilterOrGroupWithinAdvancedFilterGroup(
        {
          filterGroup: item,
          grid: advancedFilterGroup.grid,
          id: advancedFilterGroup.id,
          name: advancedFilterGroup.name,
          saved: advancedFilterGroup.saved
        },
        filterId
      );
      const index = filterGroup.filtersAndGroups.indexOf(item);
      filterGroup.filtersAndGroups[index] = filteredGroup.filterGroup;
    }
    return true;
  });

  return advancedFilterGroup;
}

export function onSetSelectedAdvancedFilterGroupOperator(
  state: SharedFilterState,
  groupID: number,
  newOperator: FilterOperations
): SharedFilterState {
  const selectedAdvancedFilterGroup = getCopyOfAdvancedFilterGroup(
    state.selectedAdvancedFilterGroup,
    false,
    new Date(new Date().setMonth(new Date().getMonth() + 1))
  );
  const updateGroupOperatorById = (filterGroup: IFilterGroup, targetID: number, operator: FilterOperations): IFilterGroup => {
    if (filterGroup.id === targetID) {
      return {
        ...filterGroup,
        operator
      };
    }
    return {
      ...filterGroup,
      filtersAndGroups: filterGroup.filtersAndGroups.map((item) => {
        if ('filtersAndGroups' in item) {
          return updateGroupOperatorById(item , targetID, operator);
        }
        return item;
      })
    };
  };
  selectedAdvancedFilterGroup.filterGroup = updateGroupOperatorById(
    selectedAdvancedFilterGroup.filterGroup,
    groupID,
    newOperator
  );
  const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
  const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === selectedAdvancedFilterGroup.id);
  advancedFilterGroups[index] = selectedAdvancedFilterGroup;
  return {
    ...state,
    advancedFilterGroups,
    selectedAdvancedFilterGroup
  };
}
